import React, { useState } from 'react';
import { Lock, Save, Eye, EyeOff } from 'lucide-react';
import PasswordStrengthIndicator from '../../shared/PasswordStrengthIndicator';
import { validatePasswordForm } from '../../../utils/passwordValidation';

const PasswordSettings = ({ formData, setFormData, onSave, isLoading = false }) => {
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    
    // Clear field-specific errors when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSave = () => {
    // Validate password form
    const validation = validatePasswordForm(formData);
    
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }
    
    // Clear errors and call onSave
    setErrors({});
    onSave();
  };

  return (
    <div className="rounded-2xl border border-gray-200/80 bg-white dark:border-gray-700 dark:bg-gray-900 shadow-sm p-4 sm:p-5 md:p-6">
      <div className="flex items-center gap-2.5 mb-4">
        <div className="w-9 h-9 rounded-xl bg-sky-50 dark:bg-sky-950/40 border border-sky-100 dark:border-sky-800 flex items-center justify-center flex-shrink-0">
          <Lock className="w-4 h-4 text-sky-700 dark:text-sky-300" />
        </div>
        <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100">Password</h2>
      </div>
      <div className="space-y-3 sm:space-y-4">
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 sm:mb-2">
            Current Password
          </label>
          <div className="relative">
            <input
              type={showCurrentPassword ? 'text' : 'password'}
              value={formData.currentPassword || ''}
              onChange={(e) => handleInputChange('currentPassword', e.target.value)}
              className={`w-full px-3 py-2 sm:px-4 sm:py-2.5 pr-10 text-sm sm:text-base border bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 !rounded-xl focus:outline-none focus:ring-2 ${
                errors.currentPassword 
                  ? 'border-red-500 focus:border-red-500 focus:ring-red-500' 
                  : 'border-gray-200 dark:border-gray-600 focus:ring-sky-500/40 focus:border-sky-500'
              }`}
            />
            <button
              type="button"
              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              {showCurrentPassword ? (
                <EyeOff className="h-4 w-4 sm:h-5 sm:w-5" />
              ) : (
                <Eye className="h-4 w-4 sm:h-5 sm:w-5" />
              )}
            </button>
          </div>
          {errors.currentPassword && (
            <p className="mt-1 text-xs sm:text-sm text-red-600 dark:text-red-400">
              {typeof errors.currentPassword === 'string' ? errors.currentPassword : errors.currentPassword[0]}
            </p>
          )}
        </div>
        
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 sm:mb-2">
            New Password
          </label>
          <div className="relative">
            <input
              type={showNewPassword ? 'text' : 'password'}
              value={formData.newPassword || ''}
              onChange={(e) => handleInputChange('newPassword', e.target.value)}
              className={`w-full px-3 py-2 sm:px-4 sm:py-2.5 pr-10 text-sm sm:text-base border bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 !rounded-xl focus:outline-none focus:ring-2 ${
                errors.newPassword 
                  ? 'border-red-500 focus:border-red-500 focus:ring-red-500' 
                  : 'border-gray-200 dark:border-gray-600 focus:ring-sky-500/40 focus:border-sky-500'
              }`}
            />
            <button
              type="button"
              onClick={() => setShowNewPassword(!showNewPassword)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              {showNewPassword ? (
                <EyeOff className="h-4 w-4 sm:h-5 sm:w-5" />
              ) : (
                <Eye className="h-4 w-4 sm:h-5 sm:w-5" />
              )}
            </button>
          </div>
          {errors.newPassword && (
            <p className="mt-1 text-xs sm:text-sm text-red-600 dark:text-red-400">
              {typeof errors.newPassword === 'string' ? errors.newPassword : errors.newPassword[0]}
            </p>
          )}
          {/* Password Strength Indicator */}
          <PasswordStrengthIndicator password={formData.newPassword || ''} className="mt-2" />
        </div>
        
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 sm:mb-2">
            Confirm New Password
          </label>
          <div className="relative">
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              value={formData.confirmPassword || ''}
              onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
              className={`w-full px-3 py-2 sm:px-4 sm:py-2.5 pr-10 text-sm sm:text-base border bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 !rounded-xl focus:outline-none focus:ring-2 ${
                errors.confirmPassword 
                  ? 'border-red-500 focus:border-red-500 focus:ring-red-500' 
                  : 'border-gray-200 dark:border-gray-600 focus:ring-sky-500/40 focus:border-sky-500'
              }`}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              {showConfirmPassword ? (
                <EyeOff className="h-4 w-4 sm:h-5 sm:w-5" />
              ) : (
                <Eye className="h-4 w-4 sm:h-5 sm:w-5" />
              )}
            </button>
          </div>
          {errors.confirmPassword && (
            <p className="mt-1 text-xs sm:text-sm text-red-600 dark:text-red-400">
              {typeof errors.confirmPassword === 'string' ? errors.confirmPassword : errors.confirmPassword[0]}
            </p>
          )}
        </div>
        
        <button
          type="button"
          onClick={handleSave}
          disabled={isLoading}
          className="flex items-center justify-center gap-2 bg-primary-600 text-white px-4 py-2.5 sm:px-6 sm:py-3 !rounded-xl hover:bg-primary-700 transition-colors w-full sm:w-auto text-sm sm:text-base font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save className="w-4 h-4" />
          <span>{isLoading ? 'Updating...' : 'Update Password'}</span>
        </button>
      </div>
    </div>
  );
};

export default PasswordSettings;
